import React, { useState } from 'react';
import { useImmerReducer } from "use-immer";

import {Button, Paper, Container, Typography, AppBar, Toolbar, makeStyles, createStyles, Theme} from '@material-ui/core'
import {Stepper, Step, StepLabel} from '@material-ui/core';

import ReactPlayer from 'react-player'

import './App.css';
import { rootReducer, initialState } from './store'
import { PlaylistEntry } from './store/playlist/types';
import stepDefinitions from './steps'

import Fullscreen from "react-full-screen";
import { setFullScreen } from './store/system/actions';
import { removeVideo } from './store/playlist/actions';

export default () => {

  const [state, dispatch] = useImmerReducer(rootReducer, initialState);

  const [entry, setEntry] = useState<PlaylistEntry>()
  const [isPlaying, setIsPlaying] = useState(false)
  const [endTime, setEndTime] = useState(0)
  
  // --------------------------------------------------------------------------------
  // video playback functions
  // --------------------------------------------------------------------------------

  const player: React.RefObject<ReactPlayer> = React.createRef();
  
  const playNext = () => {

    if(state.playlist.videos.length === 0) {
      setIsPlaying(false)
      return
    }

    const items = state.playlist.videos
    const entry = items[Math.floor(Math.random()*items.length)];

    setEntry(entry)

    if(state.system.playVideoOnce) {
      dispatch(removeVideo(entry!.file.name))
    }
  }

  const onReadyHandler = () => {

    if(isPlaying === false) {

      const min = Math.min( ...state.system.clipLength ),
            max = Math.max( ...state.system.clipLength )

      const secondsToPlay = Math.floor( Math.random() * (max - min+1) + min )
      const startTime = Math.floor( Math.random() * ( entry!.duration - secondsToPlay) )
      const endTime = startTime + secondsToPlay
  
      player.current!.seekTo(startTime)
  
      setEndTime(endTime)
    }
  }

  const onSeekHandler = () => {
    setIsPlaying(true)
  }

  interface progress{ played: number, playedSeconds: number, loaded: number, loadedSeconds: number }
  const onProgressHandler = (p:progress) => {
    
    if(p.playedSeconds <= endTime || isPlaying === false) 
      return

    setIsPlaying(false)
    setEntry(undefined)
    playNext()
  }
  
  // --------------------------------------------------------------------------------
  // step functions
  // --------------------------------------------------------------------------------

  const [activeStep, setActiveStep] = React.useState(0);
  const handleNext = () => {
    setActiveStep(prevActiveStep => prevActiveStep + 1);

    if(activeStep === stepDefinitions.length - 1) {
      playNext()
    }
  }

  const handleBack = () => {
    setActiveStep(prevActiveStep => prevActiveStep - 1);
  }

  const steps = stepDefinitions.map(step => step(state, dispatch))

  // --------------------------------------------------------------------------------
  // styling 
  // --------------------------------------------------------------------------------

  const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
          flexGrow: 1,
        },
        menuButton: {
          paddingTop: theme.spacing(2),
          paddingBottom: theme.spacing(2),
          textAlign: "right"
        },
        title: {
          flexGrow: 1,
        },
      }),
  );

  const classes = useStyles();
 
  return (

    <div className={classes.root}>

      <Container>

      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" className={classes.title}>
            Video randomizer 
          </Typography>
        </Toolbar>
      </AppBar>

      {activeStep < steps.length ? (
          <Paper>
            <Stepper activeStep={activeStep}>
              {steps.map((step, index) => (
                <Step key={step.label}>
                  <StepLabel>{step.label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            <Container>
                {steps[activeStep].content}
                <div className={classes.menuButton}>
                  <Button disabled={activeStep === 0} onClick={handleBack}>
                    Back
                  </Button>
                  <Button
                    disabled={state.playlist.videos.length === 0}
                    variant="contained"
                    color="primary"
                    onClick={handleNext}
                  >
                  {activeStep === steps.length - 1 ? 'Start session' : 'Next'}
                  </Button>
                </div>
            </Container>
          </Paper>
        ) : (
          <Fullscreen enabled={state.system.fullScreen} onChange={ isFull => dispatch(setFullScreen(isFull)) }>
              <ReactPlayer 
                ref={player}
                url={entry ? entry.url : ""}
                width="100%" height="100%"
                fileConfig={{ forceAudio:state.system.audioOnly }}
                playing={isPlaying}
                onSeek={onSeekHandler}
                onReady={onReadyHandler}
                onProgress={onProgressHandler}/>
          </Fullscreen>
        )}

      </Container>
    </div>
  );
}